/**
 * useCommentPositions — Measures section DOM offsets for section-aligned comment positioning.
 *
 * Uses ResizeObserver + MutationObserver on the content container to keep
 * measurements fresh when content changes (accordion expand, window resize, etc.).
 *
 * Returns: Map<sectionKey, { top: number, height: number }> relative to contentContainerRef.
 */
import { useState, useEffect, useCallback, useRef } from "react";

const useCommentPositions = (contentContainerRef, sectionOrder, deps = []) => {
  const [positions, setPositions] = useState(new Map());
  const rafRef = useRef(null);

  const measure = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      const container = contentContainerRef?.current;
      if (!container) return;

      const containerRect = container.getBoundingClientRect();
      const containerScrollTop = container.scrollTop;
      const next = new Map();

      sectionOrder.forEach(({ sectionKey }) => {
        const el = container.querySelector(`#${CSS.escape(sectionKey)}`);
        if (!el) return;
        const elRect = el.getBoundingClientRect();
        next.set(sectionKey, {
          top: elRect.top - containerRect.top + containerScrollTop,
          height: elRect.height,
        });
      });

      setPositions((prev) => {
        // Skip update if nothing changed (avoid unnecessary re-renders)
        if (prev.size === next.size) {
          let same = true;
          for (const [key, val] of next) {
            const old = prev.get(key);
            if (!old || Math.abs(old.top - val.top) > 0.5 || Math.abs(old.height - val.height) > 0.5) {
              same = false;
              break;
            }
          }
          if (same) return prev;
        }
        return next;
      });
    });
  }, [contentContainerRef, sectionOrder]);

  useEffect(() => {
    const container = contentContainerRef?.current;
    if (!container || sectionOrder.length === 0) return;

    // Initial measure
    measure();

    // ResizeObserver — covers window resize, content reflows, accordion toggles
    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(container);
    // Also observe each section element for individual height changes
    sectionOrder.forEach(({ sectionKey }) => {
      const el = container.querySelector(`#${CSS.escape(sectionKey)}`);
      if (el) resizeObserver.observe(el);
    });

    // MutationObserver — catches DOM structure changes (sections added/removed)
    const mutationObserver = new MutationObserver(measure);
    mutationObserver.observe(container, { childList: true, subtree: true });

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentContainerRef, sectionOrder, measure, ...deps]);

  return positions;
};

export default useCommentPositions;
